'use client';

import { Button } from '@jouzen/ecom-components/v3/Button';
import { LayoutGrid } from '@jouzen/ecom-components/v3/LayoutGrid';
import { List } from '@jouzen/ecom-components/v3/List';
import { Typography } from '@jouzen/ecom-components/v3/Typography';
import { useGateValue } from '@statsig/react-bindings';
import { redirect, usePathname } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { useEnvContext } from 'next-runtime-env';
import type { MessageKey } from 'types/LocaleMessageKey';

import { Event, EventType } from '@/analytics/types';
import Image from '@/app/components/Image';
import slugifyString from '@/utils/slugifyString';

const Content = () => {
  const { NEXT_PUBLIC_OURA_ENV } = useEnvContext();
  const purchaseLink =
    NEXT_PUBLIC_OURA_ENV === 'production'
      ? 'https://ouraring.cashstar.com/store/recipient'
      : 'https://ouraring.semi.cashstar.com/store/recipient';

  const balanceLink =
    NEXT_PUBLIC_OURA_ENV === 'production'
      ? 'https://balance.clutch.com/oura'
      : 'https://stage-balance.clutch.com/oura';

  const purchaseCorporateLink = 'https://totuscards.com/giftcards/oura';

  const t = useTranslations();
  const path = usePathname();
  const showUSA = useGateValue('usa_only');

  if (!showUSA) redirect('/404');

  const handleClick = async (cta: MessageKey, action: string) => {
    const slugifiedCta = slugifyString(t(cta));
    await window.ouraAnalytics.track(EventType.CTAClicked, {
      cta: slugifiedCta,
      action,
      path,
    });
  };

  return (
    <LayoutGrid>
      <div className="col-main flex flex-col gap-12 lg:col-start-4 lg:col-end-22 lg:flex-row">
        <div className="flex flex-1 flex-col border-b-2 border-sandstone-400 pb-10 lg:border-r-2 lg:border-b-0 lg:pr-10">
          <Typography Element="h2" className="mb-5 text-3xl">
            {t('gift-card-purchase-header')}
          </Typography>
          <div className="flex justify-center lg:justify-normal">
            <Image
              className="rounded-2xl"
              src="/blue-sky/gift-card/gift-cards-feature-or4.png"
              height={288}
              width={480}
              priority
            />
          </div>
          <List Element="ul" className="my-5">
            <li>{t('gift-card-bullet-1')}</li>
            <li>{t('gift-card-bullet-2')}</li>
            <li>{t('gift-card-bullet-3')}</li>
          </List>
          <Button
            className="w-full lg:w-fit lg:self-end"
            data-cy="gift-card-purchase-cta"
            rel="noopener noreferrer"
            target="_blank"
            variant="secondary-dark"
            href={purchaseLink}
            onClick={() => {
              void handleClick(
                'gift-card-purchase-cta',
                Event.D2CGiftCardPurchaseClicked,
              );
            }}
          >
            {t('gift-card-purchase-cta')}
          </Button>
        </div>
        <div className="col-main flex flex-1 flex-col lg:pb-10">
          <div className="flex flex-1 flex-col border-b-2 pb-10">
            <Typography Element="h2" className="text-3xl">
              {t('gift-card-balance-header')}
            </Typography>
            <Typography className="my-5 flex-1">
              {t('gift-card-balance-copy')}
            </Typography>
            <Button
              className="w-full lg:w-fit lg:self-end"
              data-cy="gift-card-balance-cta"
              rel="noopener noreferrer"
              target="_blank"
              variant="secondary-dark"
              href={balanceLink}
              onClick={() => {
                void handleClick(
                  'gift-card-balance-cta',
                  Event.D2CGiftCardBalanceClicked,
                );
              }}
            >
              {t('gift-card-balance-cta')}
            </Button>
          </div>
          <div className="mt-10 flex flex-1 flex-col lg:h-fit">
            <div className="lg:flex lg:flex-1 lg:flex-col">
              <Typography Element="h2" className="text-3xl">
                {t('gift-card-corporate-purchase-header')}
              </Typography>
              <Typography className="mt-5 mb-7">
                {t('gift-card-corporate-purchase-copy')}
              </Typography>
            </div>
            <Button
              className="w-full lg:w-fit lg:items-baseline lg:self-end"
              data-cy="gift-card-corporate-purchase-cta"
              rel="noopener noreferrer"
              target="_blank"
              variant="secondary-dark"
              href={purchaseCorporateLink}
              onClick={() => {
                void handleClick(
                  'gift-card-corporate-purchase-cta',
                  Event.B2BGiftCardPurchaseClicked,
                );
              }}
            >
              {t('gift-card-corporate-purchase-cta')}
            </Button>
          </div>
        </div>
      </div>
    </LayoutGrid>
  );
};

export default Content;
